import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";

import type { Area } from "@pharmacy/domain";

import { ForbiddenPage } from "@/components/layout/forbidden-page";
import { Splash } from "@/components/layout/splash";
import { useCan } from "./permissions";
import { useSession } from "./session";

/**
 * Refusing a page, not merely hiding its menu item (the phone's guard.tsx).
 *
 * Every address can be typed or shared, so a hidden menu item is not enough:
 * the page itself refuses. It shows "You do not have access to this", never a
 * blank page. The server refuses the page's requests regardless.
 */

/** The app's pages: signed out → the welcome page, remembering where they wanted to go. */
export function RequireSignedIn() {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === "restoring") return <Splash />;
  if (state.status === "signedOut") {
    // After Sign out, the next person at this computer starts from Home, not
    // from the page the last one was on.
    const next = state.left ? "/" : location.pathname + location.search;
    return <Navigate to={next === "/" ? "/welcome" : `/welcome?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Sign-in and sign-up pages: somebody already signed in goes on into the app. */
export function RequireSignedOut() {
  const { state } = useSession();
  const [params] = useSearchParams();

  if (state.status === "restoring") return <Splash />;
  if (state.status === "signedIn") return <Navigate to={safeNext(params.get("next"))} replace />;
  return <Outlet />;
}

export function RequireArea({ area, children }: { area: Area; children?: React.ReactNode }) {
  const can = useCan();
  if (!can.reach(area)) return <ForbiddenPage />;
  return children ?? <Outlet />;
}

/** Owners only: Staff, Branches, Billing, Pharmacy account, Staff activity, Needs attention. */
export function OwnerOnly({ children }: { children?: React.ReactNode }) {
  const can = useCan();
  if (!can.isOwner) return <ForbiddenPage />;
  return children ?? <Outlet />;
}

/**
 * Where to go after signing in. Only an address inside this app: a `next`
 * pointing elsewhere ("//evil.example", "https://…") would let a link send a
 * person who just signed in to someone else's page.
 */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}
